// 用户认证服务
const API_BASE_URL = '/api';

// 导入安全的存储访问工具
import { safeStorage, isVercelEnvironment } from './storage.js';


// 用户角色枚举
export const UserRole = {
  ADMIN: 'admin',
  USER: 'user'
};

// 认证服务
export const authService = {
  // 用户登录
  async login(username, password) {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        username,
        password
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '登录失败');
    }
    
    const data = await response.json();
    
    // 保存令牌和用户信息到本地存储
    safeStorage.setItem('token', data.token);
    safeStorage.setItem('user', JSON.stringify(data.user));
    
    return data.user;
  },
  
  // 用户登出
  logout() {
    safeStorage.removeItem('token');
    safeStorage.removeItem('user');
    // 确保清除所有可能的用户状态数据
    localStorage.clear(); // 清除所有本地存储，以防有其他相关数据
    
    // 如果在Vercel环境中，可能需要特殊处理
    if (isVercelEnvironment()) {
      console.log('Vercel环境中完全清除用户状态');
    }
    
    return true; // 返回成功状态，便于调用者确认操作完成
  },
  
  // 获取当前用户信息
  async getCurrentUser() {
    // 先从本地存储获取
    const userStr = safeStorage.getItem('user');
    if (!userStr) {
      return null;
    }
    
    try {
      // 从服务器获取最新用户信息
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${this.getToken()}`
        }
      });
      
      if (!response.ok) {
        // 如果令牌无效，清除本地存储
        if (response.status === 401 || response.status === 403) {
          this.logout();
          return null;
        }
        throw new Error('获取用户信息失败');
      }
      
      const user = await response.json();
      safeStorage.setItem('user', JSON.stringify(user));
      return user;
    } catch (error) {
      console.error('获取用户信息失败:', error);
      return JSON.parse(userStr); // 如果请求失败，返回本地存储的用户信息
    }
  },
  
  // 获取认证令牌
  getToken() {
    try {
      // 在Vercel环境中，可能需要特殊处理
      if (isVercelEnvironment()) {
        // 尝试从cookie或其他存储方式获取token
        const token = safeStorage.getItem('token');
        console.log('Vercel环境中获取token:', token);
        return token;
      }
      return safeStorage.getItem('token');
    } catch (error) {
      console.error('获取token时出错:', error);
      return null;
    }
  },
  
  // 检查用户是否已登录
  isLoggedIn() {
    return !!this.getToken();
  },
  
  // 检查用户是否为管理员
  isAdmin() {
    const userStr = safeStorage.getItem('user');
    if (!userStr) {
      return false;
    }
    
    const user = JSON.parse(userStr);
    return user.role === UserRole.ADMIN;
  },
  
  // 用户管理（仅管理员可用）
  
  // 获取所有用户
  async getAllUsers() {
    if (!this.isAdmin()) {
      throw new Error('权限不足');
    }
    
    const response = await fetch(`${API_BASE_URL}/users`, {
      headers: {
        'Authorization': `Bearer ${this.getToken()}`
      }
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '获取用户列表失败');
    }
    
    return await response.json();
  },
  
  // 创建新用户
  async createUser(userData) {
    if (!this.isAdmin()) {
      throw new Error('权限不足');
    }
    
    const response = await fetch(`${API_BASE_URL}/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getToken()}`
      },
      body: JSON.stringify(userData)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '创建用户失败');
    }
    
    return await response.json();
  },
  
  // 更新用户信息
  async updateUser(userId, userData) {
    if (!this.isAdmin()) {
      throw new Error('权限不足');
    }
    
    const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getToken()}`
      },
      body: JSON.stringify(userData)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '更新用户失败');
    }
    
    return await response.json();
  },
  
  // 删除用户
  async deleteUser(userId) {
    if (!this.isAdmin()) {
      throw new Error('权限不足');
    }
    
    const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${this.getToken()}`
      }
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '删除用户失败');
    }
    
    return await response.json();
  },
  
  // 修改密码
  async changePassword(userId, newPassword, currentPassword = null) {
    // 检查权限：只有管理员或用户本人可以修改密码
    const currentUser = await this.getCurrentUser();
    if (!currentUser) {
      throw new Error('未登录');
    }
    
    // 如果是普通用户修改自己的密码，需要验证当前密码
    // 如果是管理员修改他人密码，则不需要验证当前密码
    const isCurrentUser = currentUser.id == userId;
    const isAdmin = currentUser.role === UserRole.ADMIN;
    
    if (!isCurrentUser && !isAdmin) {
      throw new Error('权限不足');
    }
    
    // 构建请求体
    const requestBody = { newPassword };
    
    // 如果提供了当前密码，添加到请求体中
    if (currentPassword) {
      requestBody.currentPassword = currentPassword;
    }
    
    const response = await fetch(`${API_BASE_URL}/users/${userId}/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getToken()}`
      },
      body: JSON.stringify(requestBody)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '修改密码失败');
    }
    
    return await response.json();
  }
};